import { type NonZeroVector, type Point, point, vector } from '../elements';
import { EDGE_DEFAULT_THICKNESS } from '../graphConstants';
import { type NonEmptyArray, nonEmptyArray } from '../types/NonEmptyArray';
import type { EdgeData, EdgeDirection } from './EdgeData';
import type { EdgeLabels } from './EdgeLabels';
import {
  type EdgePositions,
  type EdgePositionsArrowHead,
  type EdgePositionsCircle,
  type EdgePositionsLine,
  getEdgeArrowHeadsList,
  isEdgeLineQuadraticBezier,
} from './EdgePositions';

export interface EdgeDataForPositions {
  circle?: boolean;
  direction: EdgeDirection;
  labels: EdgeLabels | undefined;
  shift: number;
  thickness: number;
}

export const dataForPositionsFromEdge = (
  edgeData: EdgeData,
): EdgeDataForPositions => ({
  circle: edgeData.circle,
  direction: edgeData.direction,
  labels: edgeData.labels,
  shift: edgeData.shift,
  thickness: edgeData.style?.line?.thickness ?? EDGE_DEFAULT_THICKNESS,
});

export function getArrowHeadPoints(
  tipPoint: Point,
  direction: NonZeroVector,
  width: number,
  height: number,
): EdgePositionsArrowHead {
  const baseMiddlePoint = point.createOnVector(tipPoint, direction, height);
  const v = vector.setMagnitude(vector.perp(direction), width / 2);

  return [
    tipPoint,
    point.createOnVector(baseMiddlePoint, v),
    point.createOnVector(baseMiddlePoint, vector.reverse(v)),
  ];
}

function getArrowHeadsPoints(arrowHeads: EdgePositionsArrowHead[]): Point[] {
  return (
    arrowHeads.reduce<Point[]>((acc, arrowHead) => acc.concat(arrowHead), []) ??
    []
  );
}

function getEdgeLinePoints(line: EdgePositionsLine): NonEmptyArray<Point> {
  return [
    line[0],
    ...line.slice(1).reduce<Point[]>((acc, element) => {
      if (isEdgeLineQuadraticBezier(element)) {
        acc.push(element.c, element.e);
      } else {
        acc.push(element);
      }
      return acc;
    }, []),
  ];
}

function getCircleBoundsPoints({
  position,
  radius,
}: EdgePositionsCircle): Point[] {
  return [
    { x: position.x - radius, y: position.y - radius },
    { x: position.x - radius, y: position.y + radius },
    { x: position.x + radius, y: position.y - radius },
    { x: position.x + radius, y: position.y + radius },
  ];
}

/**
 * Returned points' convex hull surely covers the edge with all its hitarea elements (therefore except labels)
 */
export function getEdgeConvexHullPoints(
  positions: EdgePositions,
): NonEmptyArray<Point> {
  return nonEmptyArray.concat(
    getEdgeLinePoints(positions.line),
    getArrowHeadsPoints(getEdgeArrowHeadsList(positions)),
    positions.circle ? getCircleBoundsPoints(positions.circle) : [],
  );
}

export const testable = {
  getEdgeLinePoints,
};
